import { AccordionPanel } from './AccordionPanel';
import { ColorKey } from '../models/relevance';
import { CountedValue } from '../models/facets';

import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { createRef, useEffect, useMemo, useState } from 'react';
import { MD5 } from 'crypto-js';
// eslint-disable-next-line import/no-named-as-default
import AutoSizer from 'react-virtualized/dist/es/AutoSizer';
// eslint-disable-next-line import/no-named-as-default
import List from 'react-virtualized/dist/es/List';
// eslint-disable-next-line import/no-named-as-default
import { ListRowProps } from 'react-virtualized';
import styled from 'styled-components';

interface MultiSelectListProps {
  items: Array<CountedValue>;
  label: string;
  onSelected?: (value: string) => void;
  onUnselected?: (value: string) => void;
  selectedItems: Array<string>;
  isExpanded: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const FACET_LIST_ROW_HEIGHT = 25;
const FACET_LIST_MAX_HEIGHT = 175;

export function MultiSelectList(props: MultiSelectListProps): JSX.Element {
  const {
    items,
    label,
    selectedItems,
    isExpanded,
    onSelected = () => {},
    onUnselected = () => {},
    onExpandedChange = () => {},
  } = props;
  const tooltipRef = createRef<HTMLDivElement>();
  const selectedItemsSet = new Set(selectedItems);
  const [scrollToIndex, setScrollToIndex] = useState<number>(-1);

  const handleToggle = (item: string) => {
    if (selectedItemsSet.has(item)) {
      onUnselected(item);
    } else {
      onSelected(item);
    }
  };

  // Whenever the order of the items changes, scroll to top
  const termHash = MD5(items.map((it) => it.term).join('|')).toString();
  useEffect(() => {
    setScrollToIndex(0);
  }, [termHash]);

  // after scrolling to top, reset the state
  useEffect(() => {
    setScrollToIndex(-1);
  }, [scrollToIndex]);

  const countsHash = MD5(items.map((it) => it.count).join(',')).toString();

  const data = useMemo(
    () =>
      items.map((item) => ({
        item,
        selected: selectedItemsSet.has(item.term),
        title: `${item.label ?? item.term}`,
      })),
    [termHash, countsHash, selectedItems.length],
  );

  // Function to handle the scroll event for the tooltip bug.
  // Needed in order to make the tooltip disappear while scroling
  const onScroll = () => {
    // Find the tooltip element in the DOM and change its opacity
    if (tooltipRef.current) {
      // Query for the tooltip element by class name
      const tooltipElement = document.querySelector('.MuiTooltip-tooltip');
      if (tooltipElement) {
        // Cast to HTMLElement to access `style`
        (tooltipElement as HTMLElement).style.opacity = '0';
      }
    }
  };

  let title = label;
  if (selectedItems.length > 0) {
    const filters = selectedItems.length === 1 ? 'filter' : 'filters';
    title += ` (${selectedItems.length} ${filters})`;
  }

  function rowRenderer({ key, index, style }: ListRowProps): JSX.Element {
    const row = data[index];
    return (
      <StyledFormControlLabel
        key={key}
        style={style}
        control={
          <Checkbox
            disabled={row.item.count === 0}
            checked={row.selected}
            onChange={() => handleToggle(row.item.term)}
            name={row.item.term}
          />
        }
        label={
          <Tooltip
            ref={tooltipRef}
            title={row.title}
            placement="left"
            PopperProps={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 20], // Adjust the offset of the tooltip
                  },
                },
              ],
            }}
          >
            <Container colorkey={row.item.colorKey}>
              <Count>({row.item.count.toLocaleString()})</Count>
              <Term>{row.title}</Term>
            </Container>
          </Tooltip>
        }
      />
    );
  }

  return (
    <AccordionPanel
      title={title}
      isExpanded={isExpanded}
      onExpandedChange={onExpandedChange}
    >
      <AutoSizer disableHeight={true}>
        {({ width }) => (
          <FacetList
            width={width}
            height={Math.min(
              FACET_LIST_MAX_HEIGHT,
              items.length * FACET_LIST_ROW_HEIGHT,
            )}
            rowHeight={FACET_LIST_ROW_HEIGHT}
            rowCount={data.length}
            rowRenderer={rowRenderer}
            onScroll={onScroll}
            scrollToIndex={scrollToIndex}
          />
        )}
      </AutoSizer>
    </AccordionPanel>
  );
}

const FacetList = styled(List)`
  margin-bottom: 12px;
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  .MuiFormControlLabel-label {
    overflow: hidden;
    width: 100%;
  }

  .MuiCheckbox-root {
    padding: 3px;
    color: ${({ theme }) => theme.color.text.primary};
    &.Mui-disabled {
      color: ${({ theme }) => theme.color.checkbox.primary};
      opacity: 0.7;
    }
  }
`;

const Container = styled.div<{ colorkey: ColorKey | undefined }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  color: ${({ colorkey, theme }) =>
    `${colorkey ? theme.other.relevance[colorkey] : theme.color.text.primary}`};
  user-select: none;
`;

const Count = styled.span`
  flex-shrink: 0;
  margin-right: 6px;
  font-size: ${({ theme }) => theme.font.size.small};
`;

const Term = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  font-size: ${({ theme }) => theme.font.size.small};
`;
